import { useMemo } from "react";

import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { getFileTypeName } from "api-toolkit/FilesHelper";
import SkeletonTable from "components/shared/table-skeleton/table-skeleton";
import { formatDateContext } from "utility/formatters";

import { getTruncatedFilename } from "../../api-toolkit/FilesHelper";
import { DangerIcon, DeleteIcon } from "../Icons/Icons";

export default function FinancialDocumentsWorkspaceTable({
  financialDocuments,
  currentDocumentFilter,
  documents,
  isLoading,

  handleDeleteDocumentClick,
  setIsUploadFileDialogOpen,
}) {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("lg"), { noSsr: true });
  const limitChartCount = isMobile ? 30 : 50;
  // Custom filter logic by category index
  const renderFilteredCategory = (filter, idx) =>
    filter === "All" ||
    (filter === "Production" && (idx === 0 || idx === 1)) ||
    (filter === "Financials" && (idx === 2 || idx === 3)) ||
    (filter === "Know Your Customer KYC" && idx === 4);

  // Derive filtered templates
  const filteredTemplates = useMemo(() => {
    if (!financialDocuments) return [];
    const uniqueCats = _.uniq(financialDocuments.map((t) => t.category));
    return financialDocuments.filter((template) => {
      const idx = uniqueCats.indexOf(template.category);
      return renderFilteredCategory(currentDocumentFilter, idx);
    });
  }, [financialDocuments, currentDocumentFilter]);

  // Flatten into rows for consistent rowIndex for zebra stripes
  const tableRows = useMemo(() => {
    const rows = [];
    filteredTemplates.forEach((template, tIdx) => {
      const myDocs = documents?.filter((doc) => doc.document_type_name === template.document_type_name) || [];
      if (myDocs.length) {
        myDocs.forEach((doc) => rows.push({ type: "doc", template, doc }));
      } else {
        rows.push({ type: "template", template });
      }
    });
    return rows;
  }, [filteredTemplates, documents, isLoading]);

  return (
    <Table aria-label="document table" className={`${isMobile && "mobile-document-table"} document-table`}>
      <TableHead>
        <TableRow>
          <TableCell>Type</TableCell>
          <TableCell>Section</TableCell>
          <TableCell>File Name</TableCell>
          <TableCell>Date / Sub Header</TableCell>
          <TableCell>Action</TableCell>
        </TableRow>
      </TableHead>
      {isLoading ? (
        isMobile ? (
          <TableBody>
            <TableRow>
              <TableCell>
                <SkeletonTable skeletonTableRows={1} skeletonTableColumn={1} />
              </TableCell>
              <TableCell>
                <SkeletonTable skeletonTableRows={1} skeletonTableColumn={1} />
              </TableCell>
              <TableCell>
                <SkeletonTable skeletonTableRows={1} skeletonTableColumn={1} />
              </TableCell>
              <TableCell>
                <SkeletonTable skeletonTableRows={1} skeletonTableColumn={1} />
              </TableCell>
              <TableCell>
                <SkeletonTable skeletonTableRows={1} skeletonTableColumn={1} />
              </TableCell>
            </TableRow>
          </TableBody>
        ) : (
          <TableBody>
            <TableRow>
              <TableCell colSpan={5}>
                <SkeletonTable skeletonTableRows={5} skeletonTableColumn={5} />
              </TableCell>
            </TableRow>
          </TableBody>
        )
      ) : (
        <TableBody>
          {tableRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center">
                No documents found for "{currentDocumentFilter}".
              </TableCell>
            </TableRow>
          ) : (
            tableRows.map((row, idx) => {
              const isEven = idx % 2 === 0;
              const bgColor = isEven ? "background.paper" : "grey.100";

              if (row.type === "doc") {
                const { doc, template } = row;
                const fileTypeName = getFileTypeName(doc);
                return (
                  <TableRow key={idx} sx={{ backgroundColor: bgColor }}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Typography
                          variant="body1"
                          color="textPrimary"
                          sx={{ fontWeight: "bold", textTransform: "uppercase" }}
                        >
                          {doc.document_type_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography color="textPrimary" sx={{ fontWeight: "bold", textTransform: "capitalize" }}>
                        {template.category}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color="textSecondary" sx={{ fontWeight: "bold", textTransform: "capitalize" }}>
                        {getTruncatedFilename(doc?.file_name ?? template?.templateFileName, limitChartCount) || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color="textSecondary" sx={{ fontWeight: "bold", textTransform: "capitalize" }}>
                        {fileTypeName !== "Other" ? formatDateContext(doc.lastupdate) : "Entered Manually"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <DeleteIcon
                        sx={{ color: "palette.error.light", cursor: "pointer", fontSize: "1.5rem" }}
                        onClick={() => handleDeleteDocumentClick(doc)}
                      />
                    </TableCell>
                  </TableRow>
                );
              }

              // template row when no doc
              return (
                <TableRow key={idx} sx={{ backgroundColor: bgColor }}>
                  <TableCell>
                    <Typography
                      variant="body1"
                      color="textPrimary"
                      sx={{ fontWeight: "bold", textTransform: "uppercase" }}
                    >
                      {row.template.document_type_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography color="textPrimary" sx={{ fontWeight: "bold", textTransform: "capitalize" }}>
                      {row.template.category}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      sx={{
                        borderColor: "green",
                        color: "green",
                        borderRadius: "20px",
                        width: "200px",
                        minHeight: "unset",
                      }}
                      onClick={() => setIsUploadFileDialogOpen(true)}
                    >
                      Upload File
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Typography color="textSecondary" sx={{ fontWeight: "bold", textTransform: "capitalize" }}>
                      {getTruncatedFilename(row.template.subHeader, limitChartCount) || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <DangerIcon sx={{ fontSize: "1.5rem", color: "palette.text.disabled", opacity: 0.7 }} />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      )}
    </Table>
  );
}
